// import 'font-awesome/css/font-awesome.css';
// import 'line-awesome/dist/line-awesome/css/line-awesome.css';
import 'line-awesome/dist/font-awesome-line-awesome/css/all.css';
import 'typeface-source-sans-pro/index.css';
import './assets/styles/master.scss';
import React from 'react';
import { connect } from 'react-redux';
import { matchPath } from 'react-router-dom';
import { push } from 'connected-react-router';
import { compose, lifecycle, withHandlers, withProps } from 'recompose';
import { ErrorMessage, Utils } from '@kineticdata/bundle-common';
import { I18n, bundle } from '@kineticdata/react';
import { actions } from './redux/modules/app';
import { actions as catalogActions } from './redux/modules/catalog';
import { actions as homeActions } from './redux/modules/home';

import LayoutRenderer from './components/layout';
import { AppProvider } from './AppProvider';
// Import available packages
import ServicesApp from '@kineticdata/bundle-services-custom';
// import QueueApp from '@kineticdata/bundle-queue';
// import TechBarApp from '@kineticdata/bundle-tech-bar';
import SettingsApp from '@kineticdata/bundle-settings-custom';
import SurveyApp from '@kineticdata/bundle-survey-custom';

// Mapping of Bundle Package kapp attribute values to App Components
const BUNDLE_PACKAGE_PROVIDERS = {
  services: ServicesApp,
  // queue: QueueApp,
  // 'tech-bar': TechBarApp,
  survey: SurveyApp,
};

// List of available static packages
const STATIC_PACKAGE_PROVIDERS = [SettingsApp];

// Determine the correct AppProvider based on the kapp/route
const getAppProvider = ({ kapp, pathname }) => {
  if (kapp) {
    return (
      BUNDLE_PACKAGE_PROVIDERS[
        Utils.getAttributeValue(kapp, 'Bundle Package', kapp.slug)
      ] || AppProvider
    );
  } else {
    return (
      STATIC_PACKAGE_PROVIDERS.find(provider =>
        matchPath(pathname, { path: provider.location }),
      ) || AppProvider
    );
  }
};

export const AppComponent = props =>
  !props.loading && props.errors.isEmpty() ? (
    <props.AppProvider
      appState={{
        ...props.app.toObject(),
        homeSurveyCount: props.homeSurveyCount,
        catalog: props.catalog.toObject(),
        location: props.appLocation,
        actions: {
          refreshApp: props.refreshApp,
          refreshProfile: props.refreshProfile,
          toggleFavorite: props.toggleFavoriteRequest,
          setHomeRequestsCount: props.setHomeRequestsCount,
          setHomeApprovalsCount: props.setHomeApprovalsCount,
          setHomeActionsCount: props.setHomeActionsCount,
          setHomeSurveysCount: props.setHomeSurveysCount,
          refreshCatalog: props.refreshCatalog,
          fetchSubteams: props.fetchSubteams,
          fetchUsers: props.fetchUsers,
        },
        layoutSize: props.layoutSize,
      }}
      location={props.location}
      render={appProps => (
        <LayoutRenderer
          mobile={props.layoutSize === 'small'}
          tablet={props.layoutSize !== 'xlarge'}
          shouldHideHeader={props.shouldHideHeader}
          shouldHideSidebar={props.shouldHideSidebar}
          shouldHideFooter={props.shouldHideFooter}
          shouldSuppressSidebar={props.shouldSuppressSidebar}
          {...appProps}
        />
      )}
    />
  ) : (
    !props.errors.isEmpty() && (
      <div className="p-5">
        <ErrorMessage
          title="Failed to load application"
          message={
            <>
              <div>
                <I18n>
                  Please check your network connection or contact an
                  administrator
                </I18n>
              </div>
              {props.errors.map(
                e =>
                  e && e.message ? (
                    <div>
                      <code>{e.message}</code>
                    </div>
                  ) : null,
              )}
            </>
          }
        />
      </div>
    )
  );

export const mapStateToProps = state => ({
  loading: state.app.loading,
  errors: state.app.errors,
  authenticated: state.app.authenticated,
  authRoute: state.app.authRoute,
  kapps: state.app.kapps,
  layoutSize: state.layout.size,
  kappSlug: state.app.kappSlug,
  kapp: state.app.kapp,
  location: state.router.location,
  app: state.app,
  catalog: state.catalog,
  homeSurveyCount: state.home.surveys.count,
});

export const mapDispatchToProps = {
  push,
  loadApp: actions.fetchApp,
  loadProfile: actions.fetchProfileRequest,
  toggleFavoriteRequest: actions.toggleFavoriteRequest,
  setHomeRequestsCount: homeActions.setHomeRequestsCount,
  setHomeApprovalsCount: homeActions.setHomeApprovalsCount,
  setHomeActionsCount: homeActions.setHomeActionsCount,
  setHomeSurveysCount: homeActions.setHomeSurveysCount,
  fetchHomeData: homeActions.fetchHomeDataRequest,
  loadCatalog: catalogActions.fetchCatalogRequest,
  fetchSubteams: actions.fetchSubteamsRequest,
  fetchUsers: actions.fetchUsersRequest,
};

export const App = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withHandlers({
    getLocation: ({ kappSlug }) => app =>
      `${kappSlug !== null ? `/kapps/${kappSlug}` : app.location || '/'}`,
  }),
  withProps(({ authenticated, location, kapp, profile, ...props }) => {
    const AppProvider = getAppProvider({ kapp, pathname: location.pathname });
    const appLocation = props.getLocation(AppProvider);
    const shouldHideHeader = AppProvider
      ? AppProvider.shouldHideHeader &&
        !!AppProvider.shouldHideHeader({
          appLocation,
          authenticated,
          location,
          kapp,
        })
      : true;
    const shouldHideSidebar = AppProvider
      ? AppProvider.shouldHideSidebar &&
        !!AppProvider.shouldHideSidebar({
          appLocation,
          authenticated,
          location,
          kapp,
        })
      : true;
    const shouldSuppressSidebar =
      AppProvider &&
      AppProvider.shouldSuppressSidebar &&
      !!AppProvider.shouldSuppressSidebar({
        appLocation,
        authenticated,
        location,
        kapp,
      });
    const shouldHideFooter = AppProvider
      ? AppProvider.shouldHideFooter &&
        !!AppProvider.shouldHideFooter({
          appLocation,
          authenticated,
          location,
          kapp,
        })
      : true;
    return {
      AppProvider,
      bodyClassName: AppProvider.bodyClassName || '',
      shouldHideHeader,
      shouldHideSidebar,
      shouldSuppressSidebar,
      shouldHideFooter,
      appLocation,
    };
  }),
  withHandlers({
    refreshApp: props => () => props.loadApp(),
    refreshProfile: props => () => props.loadProfile(),
    refreshCatalog: props => () => props.loadCatalog(),
  }),
  lifecycle({
    componentDidMount() {
      this.props.authenticated !== null && this.props.loadApp(true);
      if (this.props.bodyClassName) {
        document.body.className = this.props.bodyClassName;
      }
    },
    componentDidUpdate(prevProps) {
      if (this.props.authenticated !== prevProps.authenticated) {
        this.props.loadApp(true);
      }
      if (!!this.props.app.profile && !prevProps.app.profile) {
        this.props.loadCatalog();
        if (bundle.identity() === 'anonymous') {
          // If bundle identity is anonymous after user is logged in, override
          // the identity function to return the current username. This is
          // needed so that submission locking uses the correct username.
          const bundleUsername = this.props.app.profile.username;
          bundle.identity = function() {
            return bundleUsername;
          };
        }
      }
      if (
        !this.props.loading &&
        !!prevProps.loading &&
        this.props.authenticated
      ) {
        this.props.fetchHomeData(true);
      }
      if (
        !this.props.loading &&
        !this.props.authenticated &&
        !this.props.AppProvider.hasPublicRoutes
      ) {
        this.props.push(this.props.authRoute);
      }
      if (this.props.bodyClassName !== prevProps.bodyClassName) {
        document.body.className = this.props.bodyClassName;
      }
    },
  }),
)(AppComponent);
